// Mirror of src/marine_detect/alerts.py — gates on confidence, persistence,
// zone membership, and per-track-id cooldown. Conservative banner wording.

import type { AlertEvent, Detection } from "../inference/types";
import type { DangerZone } from "./danger_zone";

export const DEFAULT_REASON = "Object persisted inside forward danger zone";
export const EVENT_TYPE_FORWARD_ZONE = "forward_zone_object";

export type Clock = () => number;

export interface AlertEngineOptions {
  dangerZone: DangerZone;
  minPersistenceSeconds?: number;
  minConfidence?: number;
  cooldownSeconds?: number;
  clock?: Clock;
}

export class AlertEngine {
  dangerZone: DangerZone;
  minPersistenceSeconds: number;
  minConfidence: number;
  cooldownSeconds: number;
  clock: Clock;
  private nextSeq = 1;
  private cooldownUntil: Map<number, number> = new Map();

  constructor(opts: AlertEngineOptions) {
    this.dangerZone = opts.dangerZone;
    this.minPersistenceSeconds = opts.minPersistenceSeconds ?? 5.0;
    this.minConfidence = opts.minConfidence ?? 0.5;
    this.cooldownSeconds = opts.cooldownSeconds ?? 10.0;
    this.clock = opts.clock ?? (() => performance.now() / 1000);
  }

  /** Returns Events fired this frame. May be empty. */
  evaluate(detections: Detection[], frameSize: { w: number; h: number }): AlertEvent[] {
    const out: AlertEvent[] = [];
    const now = this.clock();
    const ts = nowIsoUtc();

    for (const det of detections) {
      if (det.track_id === null) continue;
      if (det.confidence < this.minConfidence) continue;
      if (det.seen_for_seconds === null || det.seen_for_seconds < this.minPersistenceSeconds) continue;
      const cx = (det.bbox.x1 + det.bbox.x2) / 2;
      const cy = (det.bbox.y1 + det.bbox.y2) / 2;
      if (!this.dangerZone.contains({ x: cx, y: cy }, frameSize)) continue;
      const cooldownEnd = this.cooldownUntil.get(det.track_id) ?? 0;
      if (now < cooldownEnd) continue;

      out.push({
        event_id: `evt_${String(this.nextSeq).padStart(3, "0")}`,
        type: EVENT_TYPE_FORWARD_ZONE,
        severity: "warning",
        track_id: det.track_id,
        cls_name: det.cls_name,
        confidence: det.confidence,
        seen_for_seconds: det.seen_for_seconds,
        reason: DEFAULT_REASON,
        timestamp: ts,
      });
      this.nextSeq += 1;
      this.cooldownUntil.set(det.track_id, now + this.cooldownSeconds);
    }
    return out;
  }

  /** Conservative wording: never imply distance, heading, or collision risk. */
  static formatBanner(event: AlertEvent): string {
    return (
      `Warning: ${event.cls_name} detected in forward zone ` +
      `for ${event.seen_for_seconds.toFixed(1)}s. Please verify visually.`
    );
  }
}

export function nowIsoUtc(): string {
  // ISO 8601 UTC with trailing Z, matching the spec example.
  // Strip milliseconds + the trailing offset; force a Z.
  const d = new Date();
  return (
    `${d.getUTCFullYear()}-` +
    `${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}Z`
  );
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}
